import axios from 'axios';

import { AUTH_STATE_KEY } from '@/modules/auth/constants';

import type {
	KeycloakUserInfo,
	LoginResponse,
	TokenInformation,
} from '@/modules/auth/types';
import type { IUserDB, UserRole } from '@/types/user';

const login = async (
	username: string,
	password: string
): Promise<LoginResponse> => {
	const response = await axios.post('/auth/agency/login', {
		username,
		password,
	});
	return response.data;
};

const getUserInfo = async (token: string): Promise<KeycloakUserInfo> => {
	const response = await axios.get('/auth/agency/userinfo', {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});
	return response.data;
};

const decodeJwt = (token: string): KeycloakUserInfo => {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(
		atob(base64)
			.split('')
			// biome-ignore lint/style/useTemplate: Init Biome
			.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
			.join('')
	);
	return JSON.parse(jsonPayload);
};

const mapKeycloakUserToIUserDB = (keycloakUser: KeycloakUserInfo): IUserDB => {
	return {
		id: keycloakUser.sub,
		firstname: keycloakUser.given_name ?? '',
		lastname: keycloakUser.family_name ?? '',
		email: keycloakUser.email,
		username: keycloakUser.preferred_username,
		phone: keycloakUser.phone,
		roles: keycloakUser.realm_roles as UserRole[],
		agencyId: keycloakUser.agencies?.[0]?.id,
		agenciesPopulated: keycloakUser.agencies,
	};
};

const getUserInfoFromToken = (token: string): IUserDB => {
	const keycloakUser = decodeJwt(token);
	return mapKeycloakUserToIUserDB(keycloakUser);
};

const fetchUserInfo = async (token: string): Promise<IUserDB> => {
	const keycloakUser = await getUserInfo(token);
	return mapKeycloakUserToIUserDB(keycloakUser);
};

// Forgot password flow - First send reset password request
const sendForgotPassword = async (username: string): Promise<boolean> => {
	const response = await axios.post('/auth/agency/forgot-password', {
		email: username,
	});
	return response.status === 200;
};

// Forgot password flow - Second reset password using the emailed token
// Realm (agency/worker/company) is resolved server-side from the token.
const resetPassword = async (
	token: string,
	password: string
): Promise<boolean> => {
	const response = await axios.post('/auth/password/reset', {
		token,
		newPassword: password,
	});
	return response.status === 200;
};

const updatePassword = async ({
	email,
	password,
	newPassword,
	confirmPassword,
}: {
	email: string;
	password: string;
	newPassword: string;
	confirmPassword: string;
}): Promise<boolean> => {
	const response = await axios.put('/auth/agency/users/change-password', {
		email,
		password,
		newPassword,
		confirmPassword,
	});
	return response.status === 200;
};

const register = async (
	token: string,
	username: string,
	password: string,
	confirmPassword: string
): Promise<{ token: string }> => {
	const response = await axios.post(`/user/registration/${token}`, {
		password,
		username,
		confirmPassword,
		token,
	});
	return response.data;
};

const refreshToken = async (refresh_token: string): Promise<LoginResponse> => {
	const response = await axios.post('/auth/agency/refresh-token', {
		refresh_token,
	});
	return response.data;
};

const getTokens = (): TokenInformation | null => {
	const authState = localStorage.getItem(AUTH_STATE_KEY);
	if (!authState) {
		return null;
	}
	try {
		const { access_token, refresh_token, expires_in, issued_at } =
			JSON.parse(authState);
		if (!access_token || !refresh_token || !expires_in) {
			return null;
		}
		return { access_token, refresh_token, expires_in, issued_at };
	} catch (error) {
		console.error(error);
		return null;
	}
};

const setTokens = (tokens: TokenInformation) => {
	localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(tokens));
};

const removeTokens = () => {
	localStorage.removeItem(AUTH_STATE_KEY);
};

export default {
	login,
	getUserInfoFromToken,
	fetchUserInfo,
	sendForgotPassword,
	resetPassword,
	updatePassword,
	register,
	refreshToken,
	getTokens,
	setTokens,
	removeTokens,
};
